import TaxiCorporationOrderBillingsDownloadParams, {
  TaxiCorporationOrderBillingsDownloadParamsOrder,
} from "../../../../../common/services/taxi-corporation/order-billings/download/taxi-corporation-order-billings-download-params";
import TaxiCorporationBillingType from "../../../../../common/types/taxi-corporation-billing-type";
import BillingStatus from "../../../types/billing-status";
import BillingsTaxiOrderListingFilter, {
  BillingsTaxiOrderListingBillingModelFilter,
  BillingsTaxiOrderListingBillingStatusFilter,
  BillingsTaxiOrderListingBillingTypeFilter,
  BillingsTaxiOrderListingCargoCompanyFilter,
  BillingsTaxiOrderListingDestinationTaxiFilter,
  BillingsTaxiOrderListingExcludeCargoCompanyFilter,
  BillingsTaxiOrderListingInternalOrderIdFilter,
  BillingsTaxiOrderListingOrderStartDateFilter,
  BillingsTaxiOrderListingPassengerFilter,
  BillingsTaxiOrderListingPlanEntryHumanIdsFilter,
} from "./types/billings-taxi-order-listing-filter";
import BillingsTaxiOrderListingFilterType from "./types/billings-taxi-order-listing-filter-type";
import BillingsTaxiOrderListingItemBillingStatus from "./types/billings-taxi-order-listing-item-billing-status";
import BillingsTaxiOrderListingItemBillingType from "./types/billings-taxi-order-listing-item-billing-type";
import BillingsTaxiOrderListingSortKey from "./types/billings-taxi-order-listing-sort-key";

const createBillingType = (
  status: BillingsTaxiOrderListingItemBillingType | undefined,
): TaxiCorporationBillingType | undefined => {
  switch (status) {
    case BillingsTaxiOrderListingItemBillingType.PRIV_CARGO_2_TAXI:
      return TaxiCorporationBillingType.PRIV_CARGO_2_TAXI;
    case BillingsTaxiOrderListingItemBillingType.PRIV_TAXI_2_RAILY:
      return TaxiCorporationBillingType.PRIV_TAXI_2_RAILY;
    case BillingsTaxiOrderListingItemBillingType.PUB_RAILY_2_TAXI:
      return TaxiCorporationBillingType.PUB_RAILY_2_TAXI;
    default:
      return undefined;
  }
};

const createStatus = (
  status: BillingsTaxiOrderListingItemBillingStatus | undefined,
): BillingStatus | undefined => {
  switch (status) {
    case BillingsTaxiOrderListingItemBillingStatus.ACCEPTED:
      return BillingStatus.ACCEPTED;
    case BillingsTaxiOrderListingItemBillingStatus.CREATED:
      return BillingStatus.CREATED;
    case BillingsTaxiOrderListingItemBillingStatus.REJECTED:
      return BillingStatus.REJECTED;
    case BillingsTaxiOrderListingItemBillingStatus.REOPENED:
      return BillingStatus.REOPENED;
    case BillingsTaxiOrderListingItemBillingStatus.REOPEN_REQUEST_SENT:
      return BillingStatus.REOPEN_REQUEST;
    default:
      return undefined;
  }
};

const createRequestOrder = (
  sortKey: BillingsTaxiOrderListingSortKey | null,
): TaxiCorporationOrderBillingsDownloadParamsOrder | undefined => {
  const options: {
    sortKey: BillingsTaxiOrderListingSortKey;
    requestOrder: TaxiCorporationOrderBillingsDownloadParamsOrder;
  }[] = [
    {
      requestOrder: TaxiCorporationOrderBillingsDownloadParamsOrder.DATE_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.ORDER_START_DATE_DESC,
    },
    {
      requestOrder: TaxiCorporationOrderBillingsDownloadParamsOrder.DATE_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.ORDER_START_DATE_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.DISTANCE_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.DISTANCE_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.HIGHWAY_CHARGE_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.TOLL_ROADS_COST_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.HIGHWAY_CHARGE_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.TOLL_ROADS_COST_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.HALTING_AMOUNT_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.STOPOVER_COST_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.HALTING_AMOUNT_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.STOPOVER_COST_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.HUMAN_ID_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.INTERNAL_ORDER_ID_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.HUMAN_ID_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.INTERNAL_ORDER_ID_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.BILLING_MODEL_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.BILLING_MODEL_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.BILLING_MODEL_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.BILLING_MODEL_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.DISTANCE_RATE_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_RATE_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.DISTANCE_RATE_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_RATE_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.DISTANCE_AMOUNT_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_COST_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.DISTANCE_AMOUNT_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.DISTANCE_COST_DESC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.TOTAL_AMOUNT_ASC,
      sortKey: BillingsTaxiOrderListingSortKey.TOTAL_COST_ASC,
    },
    {
      requestOrder:
        TaxiCorporationOrderBillingsDownloadParamsOrder.TOTAL_AMOUNT_DESC,
      sortKey: BillingsTaxiOrderListingSortKey.TOTAL_COST_DESC,
    },
  ];

  return options.find((option) => option.sortKey === sortKey)?.requestOrder;
};

const createRequest = (
  taxiCorporationUuid: string,
  filters: BillingsTaxiOrderListingFilter[],
  sortKey: BillingsTaxiOrderListingSortKey | null,
): TaxiCorporationOrderBillingsDownloadParams => {
  return {
    order: createRequestOrder(sortKey),
    taxiCorporationUuid,
    humanId: filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.INTERNAL_ORDER_ID,
    )?.value as
      | BillingsTaxiOrderListingInternalOrderIdFilter["value"]
      | undefined,
    planEntryHumanIds: filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.PLAN_ENTRY_HUMAN_IDS,
    )?.value as
      | BillingsTaxiOrderListingPlanEntryHumanIdsFilter["value"]
      | undefined,
    cargoCompany: filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.CARGO_COMPANY,
    )?.value as BillingsTaxiOrderListingCargoCompanyFilter["value"] | undefined,
    excludeCargoCompany: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiOrderListingFilterType.EXCLUDE_CARGO_COMPANY,
    )?.value as
      | BillingsTaxiOrderListingExcludeCargoCompanyFilter["value"]
      | undefined,
    passenger: filters.find(
      (filter) => filter.type === BillingsTaxiOrderListingFilterType.PASSENGER,
    )?.value as BillingsTaxiOrderListingPassengerFilter["value"] | undefined,
    status: createStatus(
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiOrderListingFilterType.BILLING_STATUS,
      )?.value as
        | BillingsTaxiOrderListingBillingStatusFilter["value"]
        | undefined,
    ),
    dateSince: (
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiOrderListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsTaxiOrderListingOrderStartDateFilter["value"]
        | undefined
    )?.from.toJSON(),
    dateTo: (
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiOrderListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsTaxiOrderListingOrderStartDateFilter["value"]
        | undefined
    )?.to.toJSON(),
    billingModel: filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.BILLING_MODEL,
    )?.value as BillingsTaxiOrderListingBillingModelFilter["value"] | undefined,
    billingType: createBillingType(
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiOrderListingFilterType.BILLING_TYPE,
      )?.value as
        | BillingsTaxiOrderListingBillingTypeFilter["value"]
        | undefined,
    ),
    responsibleTaxi: filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.DESTINATION_TAXI,
    )?.value as
      | BillingsTaxiOrderListingDestinationTaxiFilter["value"]
      | undefined,
  };
};

const billingsTaxiOrderListingReportRequestFactory = {
  createRequest,
};

export default billingsTaxiOrderListingReportRequestFactory;
